/**
 * Settings -> Users & roles (owner, 2026-10-29; engine side nova/auth).
 *
 * - With accounts off: what turning them on means, and the form that makes you
 *   the first Administrator.
 * - With them on: the people who can sign in (add, change role, reset a
 *   password, switch off, delete) and the roles (each a set of permissions in
 *   groups; the Administrator's is fixed, every other can be changed, copied or
 *   deleted when nobody has it).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { engineBase } from "../api";
import { useRegistry } from "../core/registry";
import { accountsOff, enableAccounts, useSession } from "../core/session";
import { toast } from "./Toast";

interface Perm { id: string; label: string; group: string; owner: string }
interface Role { id: number; name: string; builtin: string | null; isAdmin: boolean; description: string | null; permissions: string[]; users: number }
interface UserRow { id: number; username: string; displayName: string | null; roleId: number; role: string; disabled: boolean; lastLogin: string | null }

async function api<T>(path: string, body?: unknown): Promise<T & { error?: string }> {
  const res = await fetch(`${engineBase()}${path}`, body === undefined ? undefined : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  return (await res.json().catch(() => ({ error: `The engine answered ${res.status}.` }))) as T & { error?: string };
}

export function UsersSettings() {
  const s = useSession();
  if (!s.enabled) return <TurnOn />;
  return <Accounts />;
}

function TurnOn() {
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = username.trim() && password.length >= 8 && password === again;
  return (
    <>
      <p>Accounts are off: anyone who can open Nova can do everything, as before.</p>
      <p>Turned on, everyone signs in with their own name and password, and their role decides what they can do.
        Nova starts with four roles: Administrator (everything; it cannot be changed), OAP, Music editor and Advert scheduler.
        You can change those three and make your own.</p>
      <p>You become the first Administrator. Keep the password safe: only an Administrator can add people or reset passwords.</p>
      <div className="nv-user-form">
        <label><span>Your username</span><input className="nv-input" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} /></label>
        <label><span>Your name</span><input className="nv-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="As it shows in the history" /></label>
        <label><span>Password (8 characters or more)</span><input className="nv-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <label><span>The same password again</span><input className="nv-input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} /></label>
      </div>
      {password && again && password !== again && <p className="nv-signin-error">The two passwords are not the same.</p>}
      <button className="nv-btn primary" disabled={!ok || busy} onClick={() => {
        setBusy(true);
        void enableAccounts(username.trim(), password, name.trim()).then((error) => {
          setBusy(false);
          toast(error ?? "Accounts are on. You are signed in as the Administrator.", error ? "bad" : "ok");
        });
      }}>Turn on accounts</button>
    </>
  );
}

function Accounts() {
  const s = useSession();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [perms, setPerms] = useState<Perm[]>([]);
  const [denied, setDenied] = useState(false);
  const load = useCallback(async () => {
    const r = await api<{ roles: Role[]; permissions: Perm[] }>("/api/auth/roles");
    if (r.error) return setDenied(true);
    setRoles(r.roles);
    setPerms(r.permissions);
    const u = await api<{ users: UserRow[] }>("/api/auth/users");
    if (!u.error) setUsers(u.users);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  if (denied) return <p>Signed in as {s.user?.displayName} ({s.user?.role}). Only someone allowed to manage users sees this page.</p>;
  return (
    <>
      <p>Signed in as <b>{s.user?.displayName}</b> ({s.user?.role}).</p>
      <Users users={users} roles={roles} onChange={load} />
      <Roles roles={roles} perms={perms} onChange={load} />
      {s.user?.isAdmin && (
        <div className="nv-setrow">
          <div>
            <b>Turn accounts off</b>
            <span>Everyone can open Nova and do everything again, with no sign-in. People and roles are kept for when you turn accounts back on.</span>
          </div>
          <button className="nv-btn sm" onClick={() => {
            if (!window.confirm("Turn accounts off? Anyone who can open Nova will be able to do everything.")) return;
            void api("/api/auth/disable", {}).then((r) => (r.error ? toast(r.error, "bad") : accountsOff()));
          }}>Turn off</button>
        </div>
      )}
    </>
  );
}

function Users({ users, roles, onChange }: { users: UserRow[]; roles: Role[]; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState<number | null>(null);
  // What plugins add to each person (Radio: live hours); one open at a time.
  const tools = useRegistry().userTools;
  const [open, setOpen] = useState<{ user: number; tool: string } | null>(null);
  const save = async (body: Record<string, unknown>, done = "Saved.") => {
    const r = await api("/api/auth/users/save", body);
    toast(r.error ?? done, r.error ? "bad" : "ok");
    if (!r.error) onChange();
    return !r.error;
  };
  return (
    <section className="nv-users">
      <h3>People</h3>
      <table className="nv-table">
        <thead><tr><th>Username</th><th>Name</th><th>Role</th><th>Last signed in</th><th /></tr></thead>
        <tbody>
          {users.map((u) => [
            <tr key={u.id} className={u.disabled ? "nv-dim" : ""}>
              <td>{u.username}{u.disabled ? " (switched off)" : ""}</td>
              <td>{u.displayName ?? ""}</td>
              <td>
                <select className="nv-select" aria-label={`Role of ${u.username}`} value={u.roleId}
                  onChange={(e) => void save({ id: u.id, roleId: Number(e.target.value) }, `${u.username} is now ${roles.find((r) => r.id === Number(e.target.value))?.name}.`)}>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </td>
              <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : "never"}</td>
              <td className="nv-user-cell"><div className="nv-user-actions">
                <button className="nv-btn sm" onClick={() => {
                  const next = window.prompt(`New password for ${u.username} (8 characters or more):`);
                  if (next) void save({ id: u.id, password: next }, `${u.username}'s password is changed; they sign in again.`);
                }}>Reset password</button>
                {tools.map((t) => {
                  const on = open?.user === u.id && open.tool === t.id;
                  return (
                    <button key={t.id} className="nv-btn sm" title={t.title} aria-expanded={on}
                      onClick={() => setOpen(on ? null : { user: u.id, tool: t.id })}>{t.label(u)}</button>
                  );
                })}
                <button className="nv-btn sm" onClick={() => void save({ id: u.id, disabled: !u.disabled }, u.disabled ? `${u.username} can sign in again.` : `${u.username} can no longer sign in.`)}>
                  {u.disabled ? "Switch on" : "Switch off"}
                </button>
                <button className="nv-btn sm" onClick={() => {
                  if (!window.confirm(`Delete ${u.username}? Their edits stay in the history under their name.`)) return;
                  void api("/api/auth/users/delete", { id: u.id }).then((r) => {
                    toast(r.error ?? `${u.username} is deleted.`, r.error ? "bad" : "ok");
                    if (!r.error) onChange();
                  });
                }}>Delete</button>
              </div></td>
            </tr>,
            open?.user === u.id && (
              <tr key={`${u.id}-${open.tool}`}>
                <td colSpan={5} className="nv-user-cell">
                  {tools.find((t) => t.id === open.tool)?.render(u, () => setOpen(null))}
                </td>
              </tr>
            ),
          ])}
        </tbody>
      </table>
      {adding ? (
        <div className="nv-user-form">
          <label><span>Username</span><input className="nv-input" value={username} onChange={(e) => setUsername(e.target.value)} /></label>
          <label><span>Name</span><input className="nv-input" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label><span>Password (8 or more)</span><input className="nv-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          <label><span>Role</span>
            <select className="nv-select" value={roleId ?? ""} onChange={(e) => setRoleId(Number(e.target.value))}>
              <option value="" disabled>Choose…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <div className="nv-user-actions">
            <button className="nv-btn primary sm" disabled={!username.trim() || password.length < 8 || roleId === null}
              onClick={() => void save({ username: username.trim(), displayName: name.trim(), password, roleId }, `${username.trim()} can now sign in.`).then((ok) => {
                if (ok) { setAdding(false); setUsername(""); setName(""); setPassword(""); setRoleId(null); }
              })}>Add</button>
            <button className="nv-btn sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : <button className="nv-btn sm" onClick={() => setAdding(true)}>Add a person</button>}
    </section>
  );
}

function Roles({ roles, perms, onChange }: { roles: Role[]; perms: Perm[]; onChange: () => void }) {
  const [selected, setSelected] = useState<number | "new" | null>(null);
  const role = typeof selected === "number" ? roles.find((r) => r.id === selected) ?? null : null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (selected === null && roles.length) setSelected(roles[0].id);
  }, [roles, selected]);
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description ?? "");
      setChosen(new Set(role.permissions));
    }
  }, [role]);
  const groups = useMemo(() => {
    const out = new Map<string, Perm[]>();
    perms.forEach((p) => out.set(p.group, [...(out.get(p.group) ?? []), p]));
    return [...out.entries()];
  }, [perms]);
  const fixed = !!role?.isAdmin;
  const save = async () => {
    const r = await api<{ id: number }>("/api/auth/roles/save", {
      id: selected === "new" ? undefined : selected, name: name.trim(), description, permissions: [...chosen],
    });
    toast(r.error ?? `${name.trim()} is saved.`, r.error ? "bad" : "ok");
    if (!r.error) {
      setSelected(r.id);
      onChange();
    }
  };
  return (
    <section className="nv-roles">
      <h3>Roles</h3>
      <div className="nv-roles-pick">
        {roles.map((r) => (
          <button key={r.id} className={`nv-nav ${selected === r.id ? "on" : ""}`} onClick={() => setSelected(r.id)}>
            {r.name}<span className="nv-dim"> · {r.users} {r.users === 1 ? "person" : "people"}</span>
          </button>
        ))}
        <button className={`nv-nav ${selected === "new" ? "on" : ""}`} onClick={() => {
          setSelected("new");
          setName(role ? `${role.name} (copy)` : "New role");
          setDescription(role?.description ?? "");
          setChosen(new Set(role && !role.isAdmin ? role.permissions : []));
        }}>+ New role{role && !role.isAdmin ? ` from ${role.name}` : ""}</button>
      </div>
      {(role || selected === "new") && (
        <div className="nv-role-edit">
          {fixed ? (
            <p><b>{role?.name}</b> can do everything, including what plugins add later. This role cannot be changed.</p>
          ) : (
            <>
              <div className="nv-user-form">
                <label><span>Name</span><input className="nv-input" value={name} onChange={(e) => setName(e.target.value)} /></label>
                <label className="wide"><span>What it is for</span><input className="nv-input" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
              </div>
              {groups.map(([group, list]) => (
                <fieldset key={group} className="nv-perm-group">
                  <legend>{group}</legend>
                  {list.map((p) => (
                    <label key={p.id}>
                      <input type="checkbox" checked={chosen.has(p.id)} onChange={(e) => {
                        const next = new Set(chosen);
                        if (e.target.checked) next.add(p.id); else next.delete(p.id);
                        setChosen(next);
                      }} />
                      <span>{p.label}</span>
                    </label>
                  ))}
                </fieldset>
              ))}
              <div className="nv-user-actions">
                <button className="nv-btn primary sm" disabled={!name.trim()} onClick={() => void save()}>Save role</button>
                {role && (
                  <button className="nv-btn sm" disabled={role.users > 0} title={role.users > 0 ? "Give its people another role first" : undefined}
                    onClick={() => {
                      if (!window.confirm(`Delete the role ${role.name}?`)) return;
                      void api("/api/auth/roles/delete", { id: role.id }).then((r) => {
                        toast(r.error ?? `${role.name} is deleted.`, r.error ? "bad" : "ok");
                        if (!r.error) { setSelected(null); onChange(); }
                      });
                    }}>Delete role</button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
